import { Component, computed, effect, HostListener, inject, OnDestroy, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ImagePreviewService } from './image-preview.service';

@Component({
  selector: 'sentry-image-preview',
  standalone: true,
  imports: [MatButtonModule, MatIconModule],
  styles: [`
    .preview-img {
      max-width: 90vw;
      max-height: 90vh;
      border-radius: 8px;
      box-shadow: 0 25px 50px rgba(0,0,0,0.5);
      object-fit: contain;
      will-change: transform;
      user-select: none;
      -webkit-user-drag: none;
    }
  `],
  template: `
    @if (preview.url()) {
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
           style="touch-action:none"
           (click)="onBackdropClick($event)">

        <!-- Close -->
        <button mat-icon-button style="position:absolute;top:16px;right:16px;color:#fff;z-index:1"
                (click)="preview.close()">
          <mat-icon>close</mat-icon>
        </button>

        <!-- Zoom controls -->
        <div style="position:absolute;bottom:24px;right:24px;display:flex;flex-direction:column;gap:4px;z-index:1">
          <button mat-icon-button style="color:#fff;background:rgba(0,0,0,0.4)" (click)="zoomIn()">
            <mat-icon>zoom_in</mat-icon>
          </button>
          <button mat-icon-button style="color:#fff;background:rgba(0,0,0,0.4)" (click)="resetZoom()" [disabled]="scale() === 1 && tx() === 0 && ty() === 0">
            <mat-icon>fit_screen</mat-icon>
          </button>
          <button mat-icon-button style="color:#fff;background:rgba(0,0,0,0.4)" (click)="zoomOut()">
            <mat-icon>zoom_out</mat-icon>
          </button>
        </div>

        <!-- Scale indicator -->
        @if (scale() !== 1) {
          <span style="position:absolute;top:16px;left:50%;transform:translateX(-50%);color:#fff;font-size:13px;opacity:0.7;background:rgba(0,0,0,0.4);padding:2px 10px;border-radius:12px">
            {{ (scale() * 100).toFixed(0) }}%
          </span>
        }

        <img [src]="preview.url()!" [alt]="preview.label()"
             class="preview-img"
             [style.transform]="transform()"
             [style.cursor]="scale() > 1 ? (dragging() ? 'grabbing' : 'grab') : 'zoom-in'"
             [style.transition]="dragging() || pinching() ? 'none' : 'transform 0.15s ease'"
             (mousedown)="onMouseDown($event)"
             (touchstart)="onTouchStart($event)"
             (touchmove)="onTouchMove($event)"
             (touchend)="onTouchEnd($event)"
             (dblclick)="onDblClick()"
             (click)="$event.stopPropagation()">

        @if (preview.label() && scale() === 1) {
          <span style="position:absolute;bottom:24px;left:0;right:0;text-align:center;color:#fff;font-size:14px;opacity:0.8;pointer-events:none">
            {{ preview.label() }}
          </span>
        }
      </div>
    }
  `,
})
export class ImagePreviewComponent implements OnDestroy {
  preview = inject(ImagePreviewService);

  scale    = signal(1);
  tx       = signal(0);
  ty       = signal(0);
  dragging = signal(false);
  pinching = signal(false);

  transform = computed(() => `translate(${this.tx()}px, ${this.ty()}px) scale(${this.scale()})`);

  private dragStart  = { x: 0, y: 0, tx: 0, ty: 0 };
  private pinchStart = { dist: 0, scale: 0 };
  private mouseMoveF!: (e: MouseEvent) => void;
  private mouseUpFn!: (e: MouseEvent) => void;

  private readonly wheelFn = (e: WheelEvent) => {
    if (!this.preview.url()) return;
    e.preventDefault();
    const factor = e.ctrlKey ? 0.05 : 0.12;
    this.setScale(this.scale() + (e.deltaY < 0 ? factor : -factor));
  };

  constructor() {
    document.addEventListener('wheel', this.wheelFn, { passive: false });
    effect(() => {
      if (this.preview.url()) this.resetZoom();
    });
  }

  ngOnDestroy() {
    document.removeEventListener('wheel', this.wheelFn);
  }

  @HostListener('document:keydown.escape')
  onEscape() { this.preview.close(); }

  @HostListener('document:keydown.+')
  @HostListener('document:keydown.=')
  onKeyZoomIn()  { if (this.preview.url()) this.zoomIn(); }

  @HostListener('document:keydown.-')
  onKeyZoomOut() { if (this.preview.url()) this.zoomOut(); }

  onBackdropClick(e: MouseEvent) {
    if (e.target === e.currentTarget) this.preview.close();
  }

  zoomIn()  { this.setScale(this.scale() + 0.25); }
  zoomOut() { this.setScale(this.scale() - 0.25); }

  resetZoom() {
    this.scale.set(1);
    this.tx.set(0);
    this.ty.set(0);
  }

  onDblClick() {
    this.scale() > 1 ? this.resetZoom() : this.setScale(2);
  }

  onMouseDown(e: MouseEvent) {
    if (this.scale() <= 1) return;
    e.preventDefault();
    this.dragging.set(true);
    this.dragStart = { x: e.clientX, y: e.clientY, tx: this.tx(), ty: this.ty() };
    this.mouseMoveF = this.onMouseMove.bind(this);
    this.mouseUpFn  = this.onMouseUp.bind(this);
    document.addEventListener('mousemove', this.mouseMoveF);
    document.addEventListener('mouseup',   this.mouseUpFn);
  }

  private onMouseMove(e: MouseEvent) {
    this.tx.set(this.dragStart.tx + e.clientX - this.dragStart.x);
    this.ty.set(this.dragStart.ty + e.clientY - this.dragStart.y);
  }

  private onMouseUp() {
    this.dragging.set(false);
    document.removeEventListener('mousemove', this.mouseMoveF);
    document.removeEventListener('mouseup',   this.mouseUpFn);
  }

  onTouchStart(e: TouchEvent) {
    e.preventDefault();
    if (e.touches.length === 2) {
      this.pinching.set(true);
      this.pinchStart = { dist: this.touchDist(e), scale: this.scale() };
    } else if (e.touches.length === 1 && this.scale() > 1) {
      this.dragging.set(true);
      this.dragStart = { x: e.touches[0].clientX, y: e.touches[0].clientY, tx: this.tx(), ty: this.ty() };
    }
  }

  onTouchMove(e: TouchEvent) {
    e.preventDefault();
    if (e.touches.length === 2) {
      this.setScale(this.pinchStart.scale * (this.touchDist(e) / this.pinchStart.dist));
    } else if (e.touches.length === 1 && this.dragging()) {
      this.tx.set(this.dragStart.tx + e.touches[0].clientX - this.dragStart.x);
      this.ty.set(this.dragStart.ty + e.touches[0].clientY - this.dragStart.y);
    }
  }

  onTouchEnd(_e: TouchEvent) {
    this.dragging.set(false);
    this.pinching.set(false);
  }

  private setScale(v: number) {
    const next = Math.min(8, Math.max(0.5, v));
    this.scale.set(next);
    if (next <= 1) { this.tx.set(0); this.ty.set(0); }
  }

  private touchDist(e: TouchEvent) {
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
